import { AssetInsight } from "@/api/studio/insights/types/insight.type";
import { Metric } from "@/features/reports/components/metric";
import { useReportsContext } from "../../contexts/reports-context";
import { Bars3BottomLeftIcon, PhotoIcon, StarIcon } from "@heroicons/react/20/solid";
import { KeyDriversWrapper } from "./key-drivers-wrapper";
import { KeyDriverMetricTab } from "./key-driver-metric-tab";
import { MemoryRegionTable } from "./memory-region-table";
import { CopilotTile } from "./copilot-tile";
import { InfoModalTrigger } from "@/components/ui/modal";
import { AOIMemoryInfoModal } from "../../modals/aoi-memory-info-modal";
import { useSegment } from "@/hooks/use-segment";
import { MemoryMetric } from "../../contexts/reports-context";
import { MemoryScoreInfoModal } from "../../modals/memory-score-info-modal";

/* Prop Types */
interface MemoryDetailsProps {
  currentAsset: AssetInsight;
  studyId: string;
  projectId: string;
}

/*
 * MemoryDetails component
 *
 * This component displays detailed information about the memory metrics of a given asset for the in-depth report.
 */
export const MemoryDetails = ({ currentAsset, studyId, projectId }: MemoryDetailsProps) => {
  /* Context */
  const { state, actions } = useReportsContext();

  /* Consts */
  const regions = currentAsset?.memory?.regions ?? [];

  /* Hooks */
  const { segmentTrackEvent } = useSegment();

  /* Handlers */
  const handleMetricChange = (newMetric: MemoryMetric) => {
    // Segment Tracking
    segmentTrackEvent("Asset Report Metric Changed", {
      reportType: "memory",
      from: state.memoryMetric,
      to: newMetric,
      assetId: currentAsset.assetId,
      studyId,
      projectId,
    });

    // set the new metric
    actions.setMemoryMetric(newMetric);
  };

  /* Render */
  return (
    <>
      <div className="px-4 py-2 my-4">
        <Metric
          value={Math.round(currentAsset?.metrics?.memory?.score ?? 0)}
          title="Memory"
          size="xlarge"
          infoModal={<MemoryScoreInfoModal />}
          studyId={studyId}
          projectId={projectId}
          assetId={currentAsset.assetId}
          reportType="memory"
        />
        <p className="text-sm text-base-content/70 mt-2">Is the asset likely to be remembered by the viewer?</p>
      </div>

      <CopilotTile metric="memory" />

      {/* Key Drivers */}
      <KeyDriversWrapper description="The primary elements influencing overall Memory score. Other factors such as Attention, Visibility also contribute.">
        <div className="flex flex-row gap-2">
          <KeyDriverMetricTab
            onClick={() => handleMetricChange("branding")}
            metric="branding"
            metricIcon={<StarIcon className="size-4 flex-shrink-0" />}
            currentAsset={currentAsset}
            reportType="memory"
          />
          <KeyDriverMetricTab
            onClick={() => handleMetricChange("visuals")}
            metric="visuals"
            metricIcon={<PhotoIcon className="size-4 flex-shrink-0" />}
            currentAsset={currentAsset}
            reportType="memory"
          />
          <KeyDriverMetricTab
            onClick={() => handleMetricChange("text")}
            metric="text"
            metricIcon={<Bars3BottomLeftIcon className="size-4 flex-shrink-0" />}
            currentAsset={currentAsset}
            reportType="memory"
          />
        </div>
      </KeyDriversWrapper>

      {/* AOI Elements */}
      {regions.length > 0 && (
        <div className="p-4 rounded-lg bg-base-200 flex flex-col gap-3 mt-4">
          <div className="flex items-center gap-2">
            <p className="font-semibold">AOI elements</p>
            <InfoModalTrigger modalContent={<AOIMemoryInfoModal />} iconClassName="size-4" />
          </div>
          <p className="text-xs text-base-content/70">Elements that contribute towards {state.memoryMetric} score:</p>
          <MemoryRegionTable regions={regions} metric={state.memoryMetric} />
        </div>
      )}
    </>
  );
};
